import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// project imports
import useAuth from 'hooks/useAuth';
import useCatalogueVisibility from 'hooks/useCatalogueVisibility';
import useMyMenu from 'hooks/useMyMenu';
import { buildVisibilityFilter } from 'menu-items/catalogueVisibility';
import { MENU_CATALOGUE_ROUTES } from 'menu-items/catalogueRoutes';
import { getDefaultPathForRole } from './rolePath';

// ==============================|| CATALOGUE GUARD ||============================== //

/**
 * Keep a switched-off screen unreachable by URL.
 *
 * Hiding a feature from the sidebar is not a control on its own — the route still
 * resolved, so anyone who typed the path, followed an in-app link or used a
 * bookmark still got the screen. This bounces them to their landing page instead.
 *
 * Matches on the catalogue's own routes AND on MENU_CATALOGUE_ROUTES, so a page is
 * unreachable at EVERY role-prefixed URL that serves it, not just the one route the
 * catalogue stores — a switched-off page must be gone for every user regardless of
 * the path shown in Features and Pages.
 *
 * TWO gates, both subtractive and combined the same way (bounce if EITHER hides the
 * path):
 *   - the GLOBAL Features and Pages switch (`useCatalogueVisibility`), applying to
 *     everyone; and
 *   - the PER-ROLE narrowing from Role Definitions (`useMyMenu().denied_pages`) — the
 *     pages this user's role(s) were explicitly un-ticked for. Hiding those from the
 *     sidebar is not enough: most pages (a child's detail, a session sheet) have no
 *     menu item and are reached in-context, so un-ticking them had nowhere to take
 *     effect. The backend only lists a page here when an admin deliberately un-ticked
 *     it and no held role still grants it, so this can only ever remove reach.
 *
 * Deliberately a redirect rather than an error: a switched-off feature is a
 * configuration state, not a failure, and the API refuses the underlying requests
 * anyway (see require_active_feature on the backend), so nothing is exposed in the
 * moment before this runs.
 *
 * Renders children unconditionally and redirects from an effect, matching
 * AuthGuard — returning null while deciding would blank the screen on every
 * navigation while the visibility lookup is in flight.
 */
export default function CatalogueGuard({ children }) {
  const { user } = useAuth();
  const visibility = useCatalogueVisibility();
  const myMenu = useMyMenu();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // The GLOBAL Features and Pages switch.
    const globalFilter = visibility ? buildVisibilityFilter(visibility, MENU_CATALOGUE_ROUTES) : null;
    // The PER-ROLE narrowing from Role Definitions, fed into the same matcher by
    // shaping denied_pages like a visibility payload. buildVisibilityFilter returns
    // null when the list is empty, so a role that narrowed nothing costs nothing.
    const roleFilter = myMenu?.denied_pages?.length
      ? buildVisibilityFilter({ disabled_features: [], disabled_pages: myMenu.denied_pages }, MENU_CATALOGUE_ROUTES)
      : null;

    // Both lookups NOT YET KNOWN means fail open — redirecting on a pending lookup
    // would bounce people off legitimate screens on every cold load, the same rule
    // the sidebar uses.
    if (!globalFilter && !roleFilter) return;

    const hidden = (path) => Boolean((globalFilter && globalFilter.urlHidden(path)) || (roleFilter && roleFilter.urlHidden(path)));

    if (!hidden(location.pathname)) return;

    const home = getDefaultPathForRole(user?.role);
    // Never bounce to a path that is itself hidden, or the redirect loops.
    //
    // And if the fallback is hidden too, STAY PUT rather than falling through to '/'.
    // That route sits outside the dashboard layout, so landing there left a blank page
    // with no navigation at all — a worse outcome than showing a screen the role has
    // since lost, which the API refuses anyway. Bouncing someone somewhere they cannot
    // navigate away from is the one failure mode this guard must not have.
    if (hidden(home)) return;
    navigate(home, { replace: true });
  }, [visibility, myMenu, location.pathname, user, navigate]);

  return children;
}

CatalogueGuard.propTypes = { children: PropTypes.any };
